import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useImmer } from 'use-immer'
import { Sidebar, SidebarButton, Header, CircularMenuButton, Scrollable, Pagination, useIsFormSubmitting, useHandleSubmitError, SidebarSpacing, SidebarHeaderProvider } from '@/components/DashboardComponents'
import { useNavigate, useLoaderData, Form, useSubmit, useRouteLoaderData, useParams, useRevalidator, useActionData } from 'react-router'
import { Button, Textbox, Spacing, RadioGroup, RadioOption, Checkbox, TextboxList, HalfSpacing, RoundIconButton, FileLocation } from '@/form/FormComponents'
import { Icon } from '@/components/Icon'
import { FormProvider, FormObjectGroup, FormArrayGroup, FormDisabled, FormDataProvider, FormSubmitProvider } from '@/form/FormProvider';
import { Modal, ModalContent, ModalButtons, useModal, useErrorModal } from '@/components/Modal';
import { RubricEditor } from '@/pages/RubricLayout';
import { Rubrics, NavigateBackOnSuccess } from '@/pages/NewScenarioLayout';
import { compareRubricArrays } from '@/shared/utils'
import { useContinueUnlessBlocked } from '@/systems/Blocker';
import { publicAction } from '@/data/loaders';
import { useSharedWithMe } from '@/pages/Filesystem';
import { useMenu } from '@/components/Menu';
import { useBreakpoint } from '@/hooks/useIsMobile';
import { Spinner } from '@/components/Spinner';
import { useShouldShareFilesWithCoInstructorsModal } from '@/pages/Course';
import { usePreference } from '@/hooks/useStorage';

export function NewAssessmentLayout({ is_new }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("basic_details");
  const [in_rubric_editor, setInRubricEditor] = useState(false);
  const loader_data = useLoaderData().data.value;

  const scenario_data = is_new ? loader_data.data : loader_data.scenario_data;
  const scenario_id = is_new ? loader_data._id : loader_data.scenario_id;
  const initial_scenario_is_linked = is_new ? true : loader_data.scenario_is_linked;
  const [scenario_is_linked, setScenarioIsLinked] = useState(initial_scenario_is_linked);
  const scenario_edited_locally = is_new ? false : loader_data.scenario_version_id == null;
  const scenario_file = loader_data._scenario_file;

  const [initial_rubrics, updateInitialRubrics] = useImmer((loader_data.rubrics || []).map(r => (is_new ? {...r, from_scenario: true} : r)));
  const [rubrics, updateRubrics] = useImmer(initial_rubrics);

  const course = useRouteLoaderData("course").data.value;
  const submit = useSubmit();
  const form = useRef();
  const submitting = useIsFormSubmitting();
  useHandleSubmitError();
  const actionData = useActionData();
  const [seenMissingRubricWarning, setSeenMissingRubricWarning] = usePreference("seen_missing_rubric_warning", false);

  const { assessment_id } = useParams();

  const initial_data = useMemo(() => {
    if (is_new) {
      return null;
    } else {
      const { due_date, ...rest } = loader_data.data;
      const date = new Date(due_date);
      return {
        ...rest,
        ...(due_date ? {
          add_due_date: true,
          due_date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`,
          due_time: `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`,
        } : {
          add_due_date: false,
          due_date: '',
          due_time: '23:59',
        }),
      };
    }
  }, [is_new, loader_data.data]);

  useHandleSharePromptActionData({ actionData, course_id: course._id, submit });

  const [openNoRubricAssessmentWarningModal] = useModal(({ context, closeModal }) => {
    function submitForm({ formatted_data }) {
      if (formatted_data.dont_show_again) {
        setSeenMissingRubricWarning(true);
      }
      closeModal();
      context.callback();
    }

    return (
      <Modal title="Continue without a rubric?">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <div>{`A rubric is needed to generate AI feedback at the end of the assessment. Without a rubric, learners won't get any feedback.`}</div>
                  <Spacing/>
                  <Checkbox text={"Don't show again"} name="dont_show_again" default_value={true}/>
                </ModalContent>
                <ModalButtons>
                  <Button text="Go back" className="" onClick={closeModal}/>
                  <Button text="Continue anyway" className="" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  });

  function submitForm({ formatted_data }) {

    const { add_due_date, due_date, due_time, ...rest } = formatted_data;
    const new_formatted_data = {
      ...rest,
      ...(add_due_date ? {
        due_date: Date.parse(`${due_date}T${due_time}`)
      } : {
        due_date: null,
      }),
    };

    const formatted_rubrics = rubrics.map(rubric => {
      return {
        data: rubric.data,
        ...(rubric._id ? {
          _id: rubric._id,
          version_id: rubric.version_id,
          is_linked: rubric.is_linked || false,
        } : {
          local_id: rubric.local_id,
        }),
        from_scenario: rubric.from_scenario || false,
      }
    })

    const continueSubmit = () => {
      submit({ data: new_formatted_data, rubrics: formatted_rubrics, scenario_is_linked: scenario_is_linked }, { method: "post", encType: "application/json" });
    };

    if (is_new && formatted_rubrics.length === 0 && !seenMissingRubricWarning) {
      openNoRubricAssessmentWarningModal({ callback: continueSubmit });
    } else {
      continueSubmit();
    }
  }

  function handleValidateFailed({ element }) {
    setTab(element.closest("[tabname]").getAttribute("tabname"));
  }

  const goBackCallback = useCallback(() => {
    setInRubricEditor(false);
  }, [setInRubricEditor]);

  const submitCallback = useCallback(({ rubric }) => {
    if (in_rubric_editor.index != null) {
      updateRubrics(draft => {
        const file = draft[in_rubric_editor.index].file;
        draft[in_rubric_editor.index] = {
          ...rubric,
          ...((rubric._id && file) ? { file } : {}),
          ...(rubric._id ? { is_linked: true } : {}),
        };
      });
      if (rubric._id) {
        updateInitialRubrics(draft => {
          const existing_draft = draft[draft.findIndex(r => r._id == rubric._id)];
          draft[draft.findIndex(r => r._id == rubric._id)] = {
            ...existing_draft,
            version_id: rubric.version_id,
            data: rubric.data,
            // Leave .is_linked unchanged.
          };
        });
      }
    } else {
      updateRubrics(draft => {
        draft.push({
          ...rubric,
          ...(rubric._id ? {
            is_linked: true,
            file: {
              _level: "owner"
            }
          } : {})
        });
      });
    }
    setInRubricEditor(false);
  }, [in_rubric_editor, updateRubrics, setInRubricEditor, updateInitialRubrics]);

  return (
    <>
      <FormProvider onSubmit={submitForm} onValidateFailed={handleValidateFailed} initial_data={initial_data} has_changed={!compareRubricArrays(initial_rubrics, rubrics) || initial_scenario_is_linked != scenario_is_linked} unsaved_modal={actionData?.success ? null : "new_assessment"}>
        <NavigateBackOnSuccess actionData={actionData} navigate={navigate} />
        <Form id="create_assessment" method="post" ref={form} onSubmit={(event) => event.preventDefault()}>
          <div className="dashboard_container dashboard_full_height">
            {in_rubric_editor ?
              <>
                <RubricEditor goBackCallback={goBackCallback} from_scenario_editor={true} from_assessment={true} rubric={in_rubric_editor.rubric} submitCallback={submitCallback}/>
              </> :
              <>
                <header className="dashboard_topbar">
                  <div className="topbar_left">
                    <CircularMenuButton onClick={() => navigate(-1)} icon="arrow-left"/>
                    <div className="topbar_title">{initial_data ? "Edit" : "New"} assessment</div>
                  </div>
                </header>
                <div className="dashboard_main">
                  <SidebarHeaderProvider>
                    <Sidebar>
                      <SidebarButton onClick={() => {setTab("basic_details")}} name="Assessment details" selected={tab == "basic_details"}/>
                      {/* <SidebarSpacing/> */}
                      <SidebarButton onClick={() => {setTab("rubrics")}} name="Rubrics" selected={tab == "rubrics"}/>
                    </Sidebar>
                    <Scrollable className="dashboard_content" depencencies={[tab, in_rubric_editor]}>
                      <BasicDetails visible={tab == "basic_details"} editing_scenario={initial_data ? true : false} course_name={course.name} course_id={course._id} scenario_data={scenario_data} scenario_id={scenario_id} scenario_is_linked={scenario_is_linked} setScenarioIsLinked={setScenarioIsLinked} scenario_file={scenario_file} assessment_id={assessment_id} scenario_edited_locally={scenario_edited_locally} is_new={is_new} initial_scenario_is_linked={initial_scenario_is_linked}/>
                      <Rubrics visible={tab == "rubrics"} editing_scenario={initial_data ? true : false} is_assessment={true} setInRubricEditor={setInRubricEditor} rubrics={rubrics} updateRubrics={updateRubrics} is_diagnosis_case={scenario_data.diagnose || false} feedback_mode={course.feedback_mode}/>
                      <Pagination show_continue={!initial_data && tab != "rubrics"} getContainerElement={() => document.querySelector("#create_assessment")}>
                        <FormSubmitProvider>
                          {({handleSubmit, has_changed}) => {
                            if (tab == "rubrics" || initial_data) {
                              return <Button className="primary" text={initial_data ? "Save changes" : "Create assessment"} onClick={handleSubmit} disabled={submitting || (initial_data && !has_changed) || actionData?.success}/>
                            } else {
                              return <Button text="Continue" className="primary" icon="chevron-right" icon_right={true} onClick={handleSubmit}/>
                            }
                          }}
                        </FormSubmitProvider>
                      </Pagination>
                    </Scrollable>
                  </SidebarHeaderProvider>
                </div>
              </>
            }
          </div>
        </Form>
      </FormProvider>
    </>
  )
}

function BasicDetails({visible, editing_scenario, course_name, course_id, scenario_data, scenario_id, scenario_is_linked, setScenarioIsLinked, scenario_file, assessment_id, scenario_edited_locally, is_new, initial_scenario_is_linked}) {
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const [openNoPermissionModal] = useModal(({ context, closeModal }) => {
    return (
      <Modal title={`Edit just for this assessment?`}>
        <ModalContent>
          {`You don't have permission to edit the original scenario file, so any changes you make will be saved for this assessment only.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text={`Edit just for this assessment`} onClick={() => {
            closeModal();
            context.callback();
          }}/>
        </ModalButtons>
      </Modal>
    )
  });

  // const [openNoPermissionModal] = useModal(({ closeModal }) => {
  //   return (
  //     <Modal title="Your edits won't be saved back to the original scenario">
  //       <ModalContent>
  //         {`You don't have access to edit the original scenario because it's not shared with you. Any changes you make will only be saved for this assessment.`}
  //       </ModalContent>
  //       <ModalButtons>
  //         <Button text="Cancel" className="hollow" onClick={closeModal}/>
  //         <Button text="Edit just for this assessment" onClick={() => {
  //           closeModal();
  //           continueUnlessBlocked(() => {
  //             navigate(`/courses/${course_id}/assessments/${assessment_id}/edit-scenario`);
  //           });
  //         }}/>
  //       </ModalButtons>
  //     </Modal>
  //   )
  // });

  // const [openNoPermissionModal] = useModal(({ closeModal }) => {
  //   return (
  //     <Modal title="You need access">
  //       <ModalContent>
  //         {`You don't have access to edit this scenario. Ask the owner of the scenario to add you as a collaborator (coming soon).`}
  //         <br/>
  //         <br/>
  //         {`Alternatively, you can make edits just for this assessment, but any changes you make won't be saved back to the original scenario.`}
  //       </ModalContent>
  //       <ModalButtons>
  //         <Button text="Cancel" className="hollow" onClick={closeModal}/>
  //         <Button text="Edit just for this assessment" onClick={() => {
  //           closeModal();
  //           navigate(`/scenarios/a-${assessment_id}`);
  //         }}/>
  //       </ModalButtons>
  //     </Modal>
  //   )
  // });

  const [openEditLaterModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="You have unsaved changes">
        <ModalContent>
          {`Please finish creating this assessment and then come back after to edit the scenario.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Got it" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  });

  const handleClickEditScenario = () => {
    if (!scenario_edited_locally && scenario_is_linked) {
      if (["edit", "owner"].includes(scenario_file?._level)) {
        navigate(`/scenarios/${scenario_id}/edit?assessment_id=${assessment_id || "new"}&course_id=${course_id}`);
      } else {
        if (is_new) {
          openEditLaterModal();
        } else {
          openNoPermissionModal({
            callback: () => {
              navigate(`/courses/${course_id}/assessments/${assessment_id}/edit-scenario`);
            }
          });
        }
      }
    } else {
      if (is_new) {
        openEditLaterModal();
      } else {
        navigate(`/courses/${course_id}/assessments/${assessment_id}/edit-scenario`);
      }
    }
  }

  const [openSaveToMyScenariosModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitForm({ formatted_data, focusError }) {

      setLoading(true);

      publicAction("saveAssessmentScenario", {
        title: formatted_data.title,
        assessment_id: assessment_id,
        course_id: course_id,
        parent: formatted_data.parent?._id || null,
        should_link: formatted_data.should_link || false,
      }).then(({ data }) => {

        if (formatted_data.should_link) {
          revalidator.revalidate();
          setScenarioIsLinked(true);
        }
        closeModal();

      }).catch((error) => {
        console.error(error);
        setLoading(false);
        focusError({name: "title", error: "Something went wrong. Please try again."})
      })

    }

    return (
      <Modal title="Save to my scenarios">
        <FormProvider onSubmit={submitForm}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="title" title="Scenario title" onEnter={handleSubmit} default_value={scenario_data.title || ""} autofocus={true} required={"Please enter a title for this scenario"}/>
                  <Spacing/>
                  <FileLocation name="parent" title="Choose where to save" type="scenario" />
                  {context.offer_to_link && <>
                    <Spacing/>
                    <Checkbox text={`Replace the scenario in this assessment with your new saved copy, so that the files are linked together.`} name="should_link" default_value={context.offer_to_link_default}/>
                  </>}
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Save scenario" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  const { addToMyMenuItem = [] } = useSharedWithMe({ file: scenario_file, is_shared: scenario_file?._is_directly_shared, use_local_state: true, include_separator: false });

  const topMenuItems = [
    ...((!scenario_edited_locally && ["view", "edit", "owner"].includes(scenario_file?._level)) ? (
      addToMyMenuItem
    ) : [
      {
        label: 'Save to my scenarios',
        icon: 'forum-plus-outline',
        onClick: () => {openSaveToMyScenariosModal({ offer_to_link: scenario_edited_locally || !scenario_is_linked, offer_to_link_default: scenario_edited_locally })},
      }
    ])
  ]

  const bottomMenuItems = [
    ...(!scenario_edited_locally ? [
      {
        label: 'Open original file',
        icon: 'open-in-new',
        onClick: () => {window.open(`/scenarios/${scenario_id}`, "_blank")},
        disabled: !["view", "edit", "owner"].includes(scenario_file?._level),
      },
    ] : [])
  ]

  const { handleOnClick } = useMenu({
    menuItems: [
      ...topMenuItems,
      ...(topMenuItems.length > 0 && bottomMenuItems.length > 0 ? ["separator"] : []),
      ...bottomMenuItems,
    ]
  })

  const [relinkLoading, setRelinkLoading] = useState(false);

  const [openErrorModal] = useErrorModal();

  const handleRelink = () => {
    setRelinkLoading(true);

    publicAction("relinkScenarioAssessment", { id: assessment_id, course_id: course_id }).then(({ data }) => {
      revalidator.revalidate();
      setScenarioIsLinked(true);
      setRelinkLoading(false);
    }).catch((error) => {
      console.error(error);
      openErrorModal();
      setRelinkLoading(false);
    });
  }

  const { handleOnClick: handleOnClickDropdown } = useMenu({
    menuItems: scenario_is_linked ? [
      {
        heading: "Linked — Will update if original file is edited"
      },
      "separator",
      {
        label: 'Unlink',
        icon: 'link-off',
        onClick: () => {
          setScenarioIsLinked(false);
        }
      }
    ] : [
      {
        heading: `Unlinked — Edits will be just for this assessment`
      },
      "separator",
      {
        label: 'Relink and update',
        icon: 'link',
        disabled: !(["view", "edit", "owner"].includes(scenario_file?._level) || initial_scenario_is_linked),
        onClick: () => {
          if (initial_scenario_is_linked) {
            setScenarioIsLinked(true);
          } else {
            handleRelink();
          }
        }
      }
    ],
    rightHanded: false
  })

  const small_edit_button = useBreakpoint((width) => width <= 600);

  return (
    <div style={{display: (visible ? "contents" : "none")}} tabname="basic_details">
      <Header title="Assessment details"/>
      <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
        <div className="dashboard_content_container">
          {editing_scenario ? null : <>
            <label>Course</label>
            <div className="selected_scenario_box">
              <div>
                <div>
                  <Icon name="book-open-variant-outline"/>
                </div>
                <div>{course_name}</div>
              </div>
            </div>
            <Spacing/>
          </>}
          <label>Scenario</label>
          <div className="selected_scenario_box">
            <div>
              <div>
                {scenario_edited_locally ? (
                  <Icon name="forum-outline-edit"/>
                ) : (
                  <Icon name="forum-outline" style={{fill: "var(--scenario_on_elevated_color)"}}/>
                )}
              </div>
              <div>{scenario_data.title}</div>
              <div className="buttons small right">
                <Button text={small_edit_button ? "Edit" : "Edit scenario"} className="small hollow" backgroundColor="var(--header)" icon="pencil-outline" onClick={handleClickEditScenario}/>
                <RoundIconButton icon="dots-vertical" className="small" onClick={handleOnClick} />
              </div>
            </div>
            <div>
              {scenario_edited_locally ? (
                <div className="rubric_card_dropdown">
                  <div><Icon name="pencil-outline"/></div>
                  <div>{`Edited just for this assessment`}</div>
                </div>
              ) : (
                relinkLoading ? (
                  <Spinner className="rubric_card_spinner"/>
                ) : (
                  <button className="rubric_card_dropdown" onClick={handleOnClickDropdown}>
                    <div><Icon name={scenario_is_linked ? "link" : "link-off"}/></div>
                    <div>{scenario_is_linked ? "Linked to original file" : "Unlinked"}</div>
                    <div><Icon name="menu-down-enlarged"/></div>
                  </button>
                )
              )}
            </div>
          </div>
          <Spacing/>
          <Textbox name="title" title="Assessment title" autofocus={visible} required="Please enter an assessment title" hint={"Learners will see this instead of the scenario title."}/>
          <Spacing/>
          <Textbox name="description" title="Instructions (Optional)" multiline={true} markdown={true} height="long"/>
          <Spacing/>
          <Checkbox text="Due date:" name="add_due_date" default_value={false}/>
          <HalfSpacing/>
          <FormDataProvider fields={["add_due_date"]}>{({data}) => (
            <FormDisabled obviated={!data.add_due_date} indented={true}>
              <div className="due_date_time_section">
                <Textbox name="due_date" title="Date" type="date" required="Please choose date"/>
                <Textbox name="due_time" title="Time" type="time" default_value="23:59" required="Please choose time"/>
              </div>
              <HalfSpacing/>
              <div style={{display: "none"}}>
                <Checkbox name="allow_late_submissions" text="Allow late submissions" default_value={true}/>
              </div>
            </FormDisabled>
          )}</FormDataProvider>
        </div>
      </div>
    </div>
  )
}

export const useShareFilesWithCoInstructorsModal = ({ course_id }) => {
  const [openShareFilesWithCoInstructorsModal] = useModal(({ context, closeModal, setLoading }) => {
    const { share_prompt_files, callback } = context;

    const handleSubmit = function({ formatted_data }) {
      setLoading(true);

      publicAction("shareFiles", {
        ids: share_prompt_files.map(file => file.file_id).filter(file_id => share_prompt_files.length === 1 || formatted_data[file_id]),
        people: [{ course_id }],
        level: "edit",
        notify_people: false,
        message: null,
      }).then(function() {
  
        closeModal();
        callback();
  
      }).catch(function(error) {
        console.error(error);
        closeModal();
        callback();
      })
    }

    return (
      <Modal title={(share_prompt_files.length > 1 ? "Allow your co-instructors to edit these files?" : "Allow your co-instructors to edit this file?")} width={400}>
        <FormProvider onSubmit={handleSubmit}>
          <ModalContent>
            {share_prompt_files.length > 1 ? (
              <>
                <span>{"Choose whether to share "}<b style={{fontWeight: "500"}}>edit</b>{" access to these linked files:"}</span>
                <Spacing/>
                {share_prompt_files.map(file => (
                  <Checkbox key={file.file_id} name={file.file_id} text={<div className="share_file_pill">{file.type === "scenario" ? <Icon name="forum-outline" style={{fill: "var(--scenario_file_color)"}}/> : <Icon name="bullseye-arrow" style={{fill: "var(--rubric_file_color)"}}/>}<span>{file.title}</span></div>} default_value={true} ariaLabel={file.title} />
                ))}
              </>
            ) : (
              <>
                <span>{"Choose whether to share "}<b style={{fontWeight: "500"}}>edit</b>{" access to this linked file:"}</span>
                <Spacing/>
                <div style={{display: "flex"}}>
                  <div className="share_file_pill">{share_prompt_files[0].type === "scenario" ? <Icon name="forum-outline" style={{fill: "var(--scenario_file_color)"}}/> : <Icon name="bullseye-arrow" style={{fill: "var(--rubric_file_color)"}}/>}<span>{share_prompt_files[0].title}</span></div>
                </div>
              </>
            )}
          </ModalContent>
          <ModalButtons>
            <FormSubmitProvider>
              {({ handleSubmit }) => (
                <>
                  <Button text={`No thanks`} onClick={() => {
                    closeModal();
                    callback()
                  }}/>
                  <Button text={`Share edit access`} onClick={handleSubmit}/>
                </>
              )}
            </FormSubmitProvider>
          </ModalButtons>
        </FormProvider>
      </Modal>
    )
  })

  return [openShareFilesWithCoInstructorsModal];
}

export const useHandleSharePromptActionData = ({ actionData, course_id, submit }) => {
  const [openShouldShareFilesModal] = useShouldShareFilesWithCoInstructorsModal();
  const [openShareFilesWithCoInstructorsModal] = useShareFilesWithCoInstructorsModal({ course_id });
  const lastOpenSharePromptModal = useRef(null);

  useEffect(() => {
    if (actionData?.share_prompt_files && actionData.date !== lastOpenSharePromptModal.current) {
      lastOpenSharePromptModal.current = actionData.date;
      if (actionData.show_should_share_files_prompt) {
        openShouldShareFilesModal({ course_id, share_prompt_files: actionData.share_prompt_files, callback: ({ should_share_files }) => {
          if (should_share_files === "ask") {
            doShareIndividualFilesPrompt();
          } else {
            submit({ ...actionData.request_data, already_shared_files: true }, { method: "post", encType: "application/json" });
          }
        }});
      } else {
        doShareIndividualFilesPrompt();
      }

      function doShareIndividualFilesPrompt() {
        openShareFilesWithCoInstructorsModal({ share_prompt_files: actionData.share_prompt_files, callback: () => {
          submit({ ...actionData.request_data, already_shared_files: true }, { method: "post", encType: "application/json" });
        }});
      }
    }
  }, [actionData, openShouldShareFilesModal, openShareFilesWithCoInstructorsModal, submit, course_id]);
}